import React from "react";
import {
    Accordion,
    Badge,
    Box,
    Code,
    Divider,
    Group,
    Modal,
    Paper,
    ScrollArea,
    Stack,
    Text,
    Title
} from "@mantine/core";
import {IconClock, IconCoins, IconMessage, IconRobot, IconTool, IconUser} from "@tabler/icons-react";
import {SessionUsage, UsageRequestDetail} from "../types/chatTypes";

interface UsageDetailModalProps {
    opened: boolean;
    onClose: () => void;
    usage: SessionUsage | null;
}

function formatCost(cost: number): string {
    return `$${cost.toFixed(6)}`;
}

function formatTimestamp(timestamp: number): string {
    return new Date(timestamp).toLocaleTimeString();
}

function formatModel(model: string): string {
    // Simplify model names for display
    if (model.includes('opus')) return 'Opus';
    if (model.includes('sonnet')) return 'Sonnet';
    if (model.includes('haiku')) return 'Haiku';
    return model;
}

function RequestDetailCard({request, index}: { request: UsageRequestDetail; index: number }) {
    const totalTokens = request.inputTokens + request.outputTokens;

    return (
        <Accordion.Item value={`request-${index}`}>
            <Accordion.Control>
                <Group justify="space-between" wrap="nowrap" style={{width: '100%'}}>
                    <Group gap="xs">
                        <Badge size="sm" variant="light" color="blue">
                            #{index + 1}
                        </Badge>
                        <Text size="sm" c="dimmed">
                            {formatTimestamp(request.timestamp)}
                        </Text>
                        <Badge size="xs" variant="outline">
                            {formatModel(request.model)}
                        </Badge>
                    </Group>
                    <Group gap="xs">
                        <Text size="xs" c="dimmed">
                            {totalTokens.toLocaleString()} tokens
                        </Text>
                        <Text size="xs" fw={500} c="green">
                            {formatCost(request.cost)}
                        </Text>
                    </Group>
                </Group>
            </Accordion.Control>
            <Accordion.Panel>
                <Stack gap="md">
                    {/* Token breakdown */}
                    <Paper p="xs" withBorder>
                        <Group justify="space-around">
                            <Box ta="center">
                                <Text size="xs" c="dimmed">Input</Text>
                                <Text fw={500}>{request.inputTokens.toLocaleString()}</Text>
                            </Box>
                            <Box ta="center">
                                <Text size="xs" c="dimmed">Output</Text>
                                <Text fw={500}>{request.outputTokens.toLocaleString()}</Text>
                            </Box>
                            {request.cacheReadTokens > 0 && (
                                <Box ta="center">
                                    <Text size="xs" c="dimmed">Cache Read</Text>
                                    <Text fw={500} c="teal">{request.cacheReadTokens.toLocaleString()}</Text>
                                </Box>
                            )}
                            {request.cacheCreationTokens > 0 && (
                                <Box ta="center">
                                    <Text size="xs" c="dimmed">Cache Write</Text>
                                    <Text fw={500} c="orange">{request.cacheCreationTokens.toLocaleString()}</Text>
                                </Box>
                            )}
                        </Group>
                    </Paper>

                    {/* Input text */}
                    {request.inputText && (
                        <Box>
                            <Group gap="xs" mb="xs">
                                <IconUser size={14}/>
                                <Text size="sm" fw={500}>Input</Text>
                            </Group>
                            <Paper p="xs" withBorder bg="dark.8">
                                <Text size="sm" style={{whiteSpace: 'pre-wrap'}}>
                                    {request.inputText}
                                </Text>
                            </Paper>
                        </Box>
                    )}

                    {/* Tool calls */}
                    {request.toolCalls && request.toolCalls.length > 0 && (
                        <Box>
                            <Group gap="xs" mb="xs">
                                <IconTool size={14}/>
                                <Text size="sm" fw={500}>Tool Calls ({request.toolCalls.length})</Text>
                            </Group>
                            <Stack gap="xs">
                                {request.toolCalls.map((tool, i) => (
                                    <Paper key={i} p="xs" withBorder bg="dark.8">
                                        <Text size="sm" fw={500} c="yellow">{tool.name}</Text>
                                        <Code block style={{fontSize: '0.75rem', marginTop: 4}}>
                                            {JSON.stringify(tool.input, null, 2)}
                                        </Code>
                                    </Paper>
                                ))}
                            </Stack>
                        </Box>
                    )}

                    {/* Output text */}
                    {request.outputText && (
                        <Box>
                            <Group gap="xs" mb="xs">
                                <IconRobot size={14}/>
                                <Text size="sm" fw={500}>Output</Text>
                            </Group>
                            <Paper p="xs" withBorder bg="dark.8">
                                <Text size="sm" style={{whiteSpace: 'pre-wrap'}}>
                                    {request.outputText}
                                </Text>
                            </Paper>
                        </Box>
                    )}
                </Stack>
            </Accordion.Panel>
        </Accordion.Item>
    );
}

export default function UsageDetailModal({opened, onClose, usage}: UsageDetailModalProps) {
    if (!usage) return null;

    const sessionDuration = usage.startTime
        ? Math.round((Date.now() - usage.startTime) / 1000 / 60)
        : 0;

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                <Group gap="xs">
                    <IconCoins size={20}/>
                    <Title order={4}>Session Usage Details</Title>
                </Group>
            }
            size="lg"
            scrollAreaComponent={ScrollArea.Autosize}
        >
            <Stack gap="md">
                {/* Summary stats */}
                <Paper p="md" withBorder>
                    <Group justify="space-between" mb="md">
                        <Group gap="xs">
                            <IconClock size={16}/>
                            <Text size="sm" c="dimmed">Session duration: {sessionDuration} min</Text>
                        </Group>
                        <Group gap="xs">
                            <IconMessage size={16}/>
                            <Text size="sm" c="dimmed">{usage.requestCount} requests</Text>
                        </Group>
                    </Group>

                    <Group justify="space-around">
                        <Box ta="center">
                            <Text size="xs" c="dimmed">Total Input</Text>
                            <Text size="lg" fw={600}>{usage.totalInputTokens.toLocaleString()}</Text>
                            <Text size="xs" c="dimmed">tokens</Text>
                        </Box>
                        <Box ta="center">
                            <Text size="xs" c="dimmed">Total Output</Text>
                            <Text size="lg" fw={600}>{usage.totalOutputTokens.toLocaleString()}</Text>
                            <Text size="xs" c="dimmed">tokens</Text>
                        </Box>
                        <Box ta="center">
                            <Text size="xs" c="dimmed">Total Cost</Text>
                            <Text size="lg" fw={600} c="green">{usage.totalCostFormatted}</Text>
                        </Box>
                    </Group>

                    {(usage.totalCacheReadTokens > 0 || usage.totalCacheCreationTokens > 0) && (
                        <>
                            <Divider my="sm"/>
                            <Group justify="center" gap="xl">
                                {usage.totalCacheReadTokens > 0 && (
                                    <Box ta="center">
                                        <Text size="xs" c="dimmed">Cache Reads</Text>
                                        <Text fw={500} c="teal">{usage.totalCacheReadTokens.toLocaleString()}</Text>
                                    </Box>
                                )}
                                {usage.totalCacheCreationTokens > 0 && (
                                    <Box ta="center">
                                        <Text size="xs" c="dimmed">Cache Writes</Text>
                                        <Text fw={500} c="orange">{usage.totalCacheCreationTokens.toLocaleString()}</Text>
                                    </Box>
                                )}
                            </Group>
                        </>
                    )}
                </Paper>

                {/* Request history */}
                {usage.requests && usage.requests.length > 0 && (
                    <Box>
                        <Text fw={500} mb="sm">Request History</Text>
                        <Accordion variant="separated">
                            {usage.requests.map((request, index) => (
                                <RequestDetailCard
                                    key={index}
                                    request={request}
                                    index={index}
                                />
                            ))}
                        </Accordion>
                    </Box>
                )}

                {(!usage.requests || usage.requests.length === 0) && (
                    <Text c="dimmed" ta="center">No request history available</Text>
                )}
            </Stack>
        </Modal>
    );
}
